import Layout from 'layouts/Default'
import { Box, Button, Grid, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import Breadcrumbs from 'components/Breadcrumbs'
import Label from 'components/Label'
import React, { useEffect, useState } from 'react'
import { StyledBoxTitle as BoxTitle } from './styles'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import ClearIcon from '@mui/icons-material/Clear'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { useNavigate, useParams } from 'react-router-dom'
import FormInput from 'components/FormInput'
import Input from 'components/Input'
import { CreasAbrangencia, creasInit, creasSchema } from 'types/creas.type'
import { Form } from 'components/Form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import InputMask from 'components/FormInputMask'
import { ButtonSecondary } from 'components/ButtonSecondary'
import { formatZipCode } from 'utils/text.util'
import { useAddress } from 'hooks/address.hook'
import { FormInputSelect, ItemOption } from 'components/FormInputSelect'
import { useSnackBar } from 'contexts/snackbar.context'
import { useBackdrop } from 'contexts/backdrop.context'
import creaService from 'services/crea.service'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import { PhoneList } from './components/PhoneList'
import { InputSelect } from 'components/InputSelect'
import addressService from 'services/address.service'
import TableData from './components/TableData'
import { usePagination } from 'hooks/pagination.hook'
import DeleteIcon from '@mui/icons-material/Delete'
import DialogAlert from 'components/Dialog/Alert'

export const CreateCreasPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const {
    page,
    rows: { rowsPerPage, rowsOptions },
    handleNewPage,
    handleRowsPerPage,
  } = usePagination()

  const [openExcluded, setOpenExcluded] = useState<boolean>(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])

  const [abrangencia, setAbrangencia] = useState<CreasAbrangencia[]>([])
  const [data, setData] = useState<CreasAbrangencia[]>([])
  const [bairro, setBairro] = useState('')
  const [bairros, setBairros] = useState<ItemOption[]>([])
  const [bairroId, setBairroId] = useState('')
  const [cidades, setCidades] = useState<ItemOption[]>([])
  const [cidadeId, setCidadeId] = useState('')

  const methods = useForm({
    mode: 'all',
    resolver: zodResolver(creasSchema),
    values: creasInit,
  })

  const { control, handleSubmit, setValue, watch, reset } = methods

  const clearBairroAndCidade = () => {
    setBairroId('')
    setCidadeId('')
  }

  const handleSaveSubmit = async (values: any) => {
    if (abrangencia.length === 0) {
      showSnackBar(' É obrigatório informar a área de abrangência.', 'warning')
      return
    }

    clearBairroAndCidade()

    handleBackdropOpen()
    if (!id) {
      await creaService
        .create({ ...values, abrangencia })
        .then(async () => {
          showSnackBar(SystemMessages.MESSAGE_001, 'success')
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    } else {
      await creaService
        .update(id, { ...values, abrangencia })
        .then(async () => {
          showSnackBar(SystemMessages.MESSAGE_001, 'success')
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const zipCodeValue = watch('endereco.cep')
  const stateIdValue = watch('endereco.ufId')
  const cityIdValue = watch('endereco.cidadeId')

  const {
    address: { stateOptions, cityOptions, neighborhoodOptions },
    getAddressByZipCode,
  } = useAddress(stateIdValue, cityIdValue)

  const handleFillAddressFields = async (): Promise<void> => {
    if (zipCodeValue) {
      const formattedZipCode = formatZipCode(zipCodeValue)
      const res = await getAddressByZipCode(formattedZipCode)
      if (!res) return
      const neighborhoodValue = res.bairroId ? res.bairroId : '0'
      const cityValue = res.cidadeId ? res.cidadeId : '0'
      setValue('endereco.cep', res.cep, { shouldValidate: true })
      setValue('endereco.ufId', res.ufId, { shouldValidate: true })
      setValue('endereco.bairroId', neighborhoodValue, { shouldValidate: true })
      setValue('endereco.cidadeId', cityValue, { shouldValidate: true })
      setValue('endereco.logradouro', res.logradouro, { shouldValidate: true })
      setValue('endereco.complemento', res.complemento, { shouldValidate: true })
    }
  }

  const handleDisableSearchZipCode = (): boolean => {
    let disabled = true
    if (zipCodeValue && zipCodeValue.length >= 7) {
      disabled = false
    }
    return disabled
  }

  const RJ_STATE_OPTION_ID = '33'

  const fillCities = async () => {
    addressService.getAllCitiesByStateId(RJ_STATE_OPTION_ID).then((res) => setCidades(res.data))
  }

  const fillNeighborhood = async (id: string) => {
    addressService.getAllNeighbourhoodByCityId(id).then((res) => setBairros(res.data))
  }

  const handleRemoveNeighborhood = (id: string) => {
    setAbrangencia((prevState) => prevState.filter((item) => item.bairroId !== id))
  }

  const handleRemoveSelectedNeighborhood = () => {
    setOpenExcluded(false)
    console.log('selectedRows', selectedRows, abrangencia)
    setAbrangencia((prevState) =>
      prevState.filter(
        (item) => !selectedRows.some((row) => String(row) === String(item.bairroId)),
      ),
    )
  }

  const fillCreas = async () => {
    handleBackdropOpen()
    creaService
      .findById(id)
      .then((res) => {
        reset(res.data)
        setAbrangencia(res.data.abrangencia)
      })
      .finally(() => handleBackdropClose())
  }

  useEffect(() => {
    fillCities()
  }, [])

  useEffect(() => {
    console.log('abrangencia', abrangencia)
    setData(abrangencia)
  }, [abrangencia])

  useEffect(() => {
    if (id) {
      fillCreas()
    }
  }, [id])

  useEffect(() => {
    const bairrosId = abrangencia.map((item) => Number(item.bairroId))
    setSelectedRows((rows) =>
      rows.filter((selectedRow) => bairrosId.length > 0 && !bairrosId.includes(selectedRow)),
    )
  }, [abrangencia])

  const checkKeyDown = (e: any) => {
    if (e.key === 'Enter') e.preventDefault()
  }

  const pesquisarBairro = () => {
    setData(
      abrangencia.filter(
        (a) =>
          a.bairro.toLocaleLowerCase().indexOf(bairro.toLocaleLowerCase()) > -1 || bairro === '',
      ),
    )
  }

  return (
    <Layout>
      <Grid container spacing={2}>
        <Grid container item xs={12} sx={{ mb: 2 }}>
          <Breadcrumbs>
            <Label text='Gestão de CREAS' bold v={'body1'} />
          </Breadcrumbs>
        </Grid>
        <Grid item xs={12}>
          <Form
            methods={methods}
            onSubmit={handleSubmit(handleSaveSubmit)}
            onKeyDown={(e) => checkKeyDown(e)}
          >
            <Paper sx={{ padding: 4, border: '1px solid #EAEAEA' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <BoxTitle>
                    <Label text='DADOS GERAIS' v='h5' bold />
                  </BoxTitle>
                </Grid>
                <Grid item lg={12} md={12} xs={12}>
                  <Grid container spacing={2} rowSpacing={4}>
                    <Grid item xs={12}>
                      <FormInput
                        control={control}
                        name='nome'
                        label='Nome do CREAS: *'
                        placeholder='Informe o nome do CREAS'
                        maxLength={200}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FormInput
                        control={control}
                        name='diretor'
                        label='Diretor (a): *'
                        placeholder='Informe o nome do(a) diretor(a) do CREAS'
                        maxLength={60}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FormInput
                        control={control}
                        name='email'
                        label='E-mail:'
                        placeholder='Informe o e-mail'
                        maxLength={60}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Stack direction='row' spacing={1}>
                        <InputMask
                          control={control}
                          name={`endereco.cep`}
                          label='CEP *'
                          mask='00000000'
                          placeholder='Informe o CEP'
                          sx={{ flex: 1 }}
                        />
                        <ButtonSecondary
                          maxHeight={40}
                          startIcon={<SearchRoundedIcon />}
                          label='Consultar'
                          disabled={handleDisableSearchZipCode()}
                          onClick={handleFillAddressFields}
                        />
                      </Stack>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <FormInputSelect
                        control={control}
                        name={`endereco.ufId`}
                        label='Estado *'
                        options={stateOptions}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <FormInputSelect
                        control={control}
                        name={`endereco.cidadeId`}
                        label='Cidade *'
                        options={cityOptions}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <FormInputSelect
                        control={control}
                        name={`endereco.bairroId`}
                        label='Bairro *'
                        options={neighborhoodOptions}
                      />
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <FormInput
                        control={control}
                        name={`endereco.logradouro`}
                        label='Logradouro *'
                        placeholder='Informe o logradouro'
                        maxLength={60}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <InputMask
                        control={control}
                        name={`endereco.numero`}
                        label='Número'
                        placeholder={'Informe o número'}
                        mask='0000000000'
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <FormInput
                        control={control}
                        name={`endereco.complemento`}
                        label='Complemento'
                        placeholder='Informe o complemento'
                        maxLength={20}
                      />
                    </Grid>
                    <Grid item md={12} xs={12}>
                      <FormInput
                        control={control}
                        name={`endereco.pontoReferencia`}
                        label='Ponto de referência'
                        placeholder='Informe o ponto de referência.'
                        maxLength={300}
                        multiline
                        minRows={4}
                      />
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <PhoneList control={control} watch={watch} setValue={setValue} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ padding: 4, marginY: 2, border: '1px solid #EAEAEA' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <BoxTitle>
                    <Label text='ÁREA DE ABRANGÊNCIA' v='h5' bold />
                  </BoxTitle>
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputSelect
                    name={`cidadeId`}
                    label='Munícipio de abrangência:'
                    options={cidades}
                    value={cidadeId}
                    onChange={(e) => {
                      setCidadeId(e.target.value)
                      setBairroId('')
                      fillNeighborhood(e.target.value)
                    }}
                  />
                </Grid>
                <Grid item md={6} xs={12} display={'flex'} flexDirection={'row'}>
                  <InputSelect
                    name={`bairroId`}
                    label='Bairro de abrangência:'
                    options={bairros}
                    value={bairroId}
                    disabled={!cidadeId || cidadeId === '0'}
                    onChange={(e) => setBairroId(e.target.value)}
                    sx={{ width: '99%' }}
                  />

                  <Box marginLeft={1} marginTop={'1.5rem'}>
                    <Tooltip title={'Adicionar'}>
                      <Button
                        variant={'contained'}
                        color='info'
                        aria-label='add'
                        startIcon={<AddRoundedIcon />}
                        disabled={
                          bairroId === '0' ||
                          bairroId === '' ||
                          !!abrangencia.find((i) => i.bairroId === bairroId)
                        }
                        onClick={() => {
                          setAbrangencia((prevState) => [
                            ...prevState,
                            {
                              bairro:
                                bairros.find((i) => Number(i.value) === Number(bairroId))?.label ||
                                '',
                              bairroId,
                              cidade: cidades.find((i) => i.value === cidadeId)?.label || '',
                              cidadeId,
                              id: null,
                            },
                          ])

                          setCidadeId('')
                          setBairroId('')
                          setBairros([])
                        }}
                      >
                        Adicionar
                      </Button>
                    </Tooltip>
                  </Box>
                </Grid>

                <Grid item xl={2} lg={4} md={6} xs={12}>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'center'}
                    sx={{
                      padding: 1,
                      borderRadius: 4,
                      backgroundColor: '#F9EBEC',
                    }}
                  >
                    <LocationOnOutlinedIcon sx={{ marginRight: 1 }} />
                    <Typography variant={'body1'} color={'text.main'} sx={{ fontWeight: 800 }}>
                      Áreas abrangidas: {abrangencia.length}
                    </Typography>
                  </Box>
                </Grid>

                <Grid
                  item
                  xl={10}
                  lg={8}
                  md={6}
                  xs={12}
                  justifyContent={'flex-end'}
                  display={'flex'}
                >
                  <Input
                    value={bairro}
                    name='name'
                    label=''
                    placeholder='Pesquisar bairro abrangido'
                    maxLength={100}
                    onKeyDown={(e) => e.key === 'Enter' && pesquisarBairro()}
                    onChange={(e) => setBairro(e.target.value)}
                  />
                  <Tooltip title={'Pesquisar'}>
                    <IconButton
                      color='info'
                      aria-label='search'
                      sx={{ marginLeft: 1 }}
                      onClick={() => {
                        pesquisarBairro()
                      }}
                    >
                      <SearchRoundedIcon />
                    </IconButton>
                  </Tooltip>
                  {bairro !== '' && (
                    <Tooltip title={'Limpar'}>
                      <IconButton
                        color='info'
                        aria-label='clear'
                        onClick={() => {
                          setData(abrangencia)
                          setBairro('')
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {selectedRows && selectedRows.length > 0 && (
                    <Tooltip title={'Excluir Selecionados'}>
                      <IconButton color='error' onClick={() => setOpenExcluded(true)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
                {data && data.length > 0 && (
                  <Grid item xs={12}>
                    <TableData
                      data={data}
                      handleDelete={handleRemoveNeighborhood}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleNewPage={handleNewPage}
                      handleRowsPerPage={handleRowsPerPage}
                      rowsOptions={rowsOptions}
                      totalOfElements={data.length}
                      selectedRows={selectedRows}
                      setSelectedRows={setSelectedRows}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <Button
                  type={'submit'}
                  variant='contained'
                  size={'large'}
                  startIcon={<SaveRoundedIcon />}
                  color='info'
                >
                  Salvar
                </Button>
                <Box sx={{ marginLeft: '1rem' }}>
                  <Button
                    variant='outlined'
                    color={'secondary'}
                    size={'large'}
                    onClick={() => navigate(-1)}
                    startIcon={<ArrowLeftRoundedIcon />}
                  >
                    Voltar
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Form>
        </Grid>
      </Grid>
      <DialogAlert
        title='Excluir'
        contentText={`Tem certeza que deseja excluir as áreas de abrangências?`}
        open={openExcluded}
        onSuccess={handleRemoveSelectedNeighborhood}
        onClose={() => setOpenExcluded(false)}
      />
    </Layout>
  )
}
